import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { PointOfInterestChecked } from '@/types';
import * as localState from '../state';
import { SelectedPoiContent } from './SelectedPoiContent';
import { PointsOfInterestByType } from '@/components/Analysis/ReviewScreen/PointsOfInterestByType';
import * as analysisState from '@/components/Analysis/state';
import { style } from './styles';
import { useIsolateCustomBlock } from '../../CustomBlocks/hooks';

type PointOfInterestTabProps = {
  pois: PointOfInterestChecked[];
  changeTab: (selectedTabIndex: number) => void;
  allPOILoading?: boolean;
};

export const PointOfInterestTab = ({ pois, changeTab, allPOILoading }: PointOfInterestTabProps) => {
  const { deSelectCustomBlock } = useIsolateCustomBlock({});
  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    localState.selectedPointOfInterestId
  );
  const setSelectedReviewPointsOfInterest = useSetAtom(
    analysisState.selectedReviewPointsOfInterest
  );
  const setIsAllReviewPointsOfInterestSelected = useSetAtom(
    analysisState.isAllReviewPointsOfInterestSelected
  );
  const setSelectedReviewPoiTypeId = useSetAtom(analysisState.selectedReviewPoiTypeId);
  const setSelectedReviewPoiTypes = useSetAtom(analysisState.selectedReviewPoiTypes);

  const closeHandler = useCallback(() => {
    deSelectCustomBlock();
    setSelectedPointOfInterestId(undefined);
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
    return undefined;
  }, [
    deSelectCustomBlock,
    setSelectedPointOfInterestId,
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
  ]);

  // Separate handler for POI deletion that preserves the custom block selection.
  // Unlike closeHandler, this does NOT call deSelectCustomBlock() so the
  // component point cloud remains visible within the custom paint block.
  const onDeleteHandler = useCallback(() => {
    setSelectedPointOfInterestId(undefined);
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
  }, [
    setSelectedPointOfInterestId,
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
  ]);

  useEffect(() => {
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
  }, [
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
  ]);

  const selectedPoiDisplayComponent = useMemo(
    () => (
      <SelectedPoiContent
        selectedPoiId={selectedPointOfInterestId as string}
        closeHandler={closeHandler}
        onDeleteHandler={onDeleteHandler}
        sx={style}
        enableNavigation={true}
      />
    ),
    [closeHandler, onDeleteHandler, selectedPointOfInterestId]
  );

  if (selectedPointOfInterestId && selectedPointOfInterestId.length > 0) {
    return selectedPoiDisplayComponent;
  }

  return <PointsOfInterestByType pois={pois} changeTab={changeTab} allPOILoading={allPOILoading} />;
};
